import { Component, ViewChild } from '@angular/core';
import { ApiService } from 'app/api.service';
import { AppService } from 'app/app.service';
import { DecimalPipe } from 'app/shared/decimal.pipe';
import { MySQLDateToISOPipe } from 'app/shared/mysql-date-to-iso.pipe';
import { UIModalBaseComponent } from 'app/ui/ui-modal-base.component';
import { UIModalComponent } from 'app/ui/ui-modal.component';

declare const Mangler: any;

@Component({
	selector: 'app-modal-edit-task',
	templateUrl: './modal-edit-task.component.html'
})
export class ModalEditTaskComponent extends UIModalBaseComponent {

	@ViewChild('selectTaskTemplateModal') selectTaskTemplateModal: UIModalComponent;
	@ViewChild('saveAsTaskTemplateModal') saveAsTaskTemplateModal: UIModalComponent;

	id;
	details;
	disabled = false;

	subtaskSimple = 0;
	subtaskItemised = 0;

	assignedUsersSelected: any[] = []; // union of locked (automatic) + manual ids, bound to the picker

	taskTemplates;
	templateName = '';
	templateSubtasks: any[] = [];
	templatePhases: any[] = [];
	templateDependencies: any[] = [];
	templateData: any = {};
	editingTemplateId = null;

	modalData;

	get modalTitle() {
		const isNew = this.id === 'new';
		const hasParent = !!this.details?.task?.parent_id;

		if (isNew) {
			return hasParent ? 'New Sub Task' : 'New Task';
		}
		return hasParent ? 'Edit Sub Task' : 'Edit Task';
	}

	get priority() { return this.details?.task.priority; }
	set priority(value) { if (this.details?.task) this.details.task.priority = value; }

	get extractedHours(): number {
		if (!this.details?.task.extra_labour) return 0;
		return this.details.task.extra_labour.reduce(
			(sum, lt) => sum + (DecimalPipe.parse(lt.hours) || 0),
			0,
		);
	}

	get extraHours(): number {
		if (!this.details?.task) return 0;
		let hours = DecimalPipe.parse(this.details.task.duration) || 0;
		if (this.details.edit_main && this.details.task.duration_type === 'min') hours /= 60;
		return Math.max(0, hours - this.extractedHours);
	}

	constructor(
		public app: AppService,
		private api: ApiService
	) {
		super();
	}

	open(mdata: any) {
		this.id = mdata.id || 'new';
		this.modalData = mdata;

		this.details = null;
		this.taskTemplates = null;
		this.disabled = false;
		this.subtaskItemised = 0;
		this.subtaskSimple = 0;

		const success = data => {
			if (mdata.billable !== undefined && data.task) data.task.billable = mdata.billable ? 1 : 0;
			this.processData(data);
		};

		const fail = error => {
			this.app.notifications.showDanger(error.message);
		};

		if (this.id === 'new') {
			if (mdata.type === 'simple') {
				// Check if we have any task templates set
				this.api.task.listTaskTemplates(list => {
					if (list?.length) {
						// Yes, we have task templates, ask user to select one
						this.taskTemplates = list;
						this.selectTaskTemplateModal.open();
					} else {
						// No task templates
						this.api.task.new(mdata, success, fail);
					}
				}, () => {
					// Can't get task templates, new task from blank
					this.api.task.new(mdata, success, fail);
				});
			} else {
				this.api.task.new(mdata, success, fail);
			}
		} else {
			this.api.task.get(this.id, success, fail);
		}
	}

	processData(data) {
		if (data.edit_main && data.task.duration_type === 'min') data.task.duration *= 60;
		if (data.edit_lines) data.task.lines.forEach(line => { if (line.task?.duration_type === 'min') line.task.duration *= 60; });
		// New-definition subtasks (e.g. pre-filled from a template) store hours; show minutes when needed
		if (Array.isArray(data.task.subtasks)) data.task.subtasks.forEach(sub => { if (!sub.id && sub.duration_type === 'min') sub.duration *= 60; });

		this.subtaskItemised = 0;
		this.subtaskSimple = 0;
		if (data.task.subtasks) {
			data.task.subtasks.forEach(sub => {
				if (sub.type === 'itemised') this.subtaskItemised += 1;
				if (sub.type === 'simple') this.subtaskSimple += 1;
			});
		}

		this.details = data || {};
		this.details.task['start_datetime'] = MySQLDateToISOPipe.stringToDate(this.details.task['start_datetime']);

		const lockedUserIds = this.details.task.locked_users || [];
		this.assignedUsersSelected = Array.from(new Set([...lockedUserIds, ...(this.details.task.assigned_users || [])]));

		// Put categories into category groups
		this.details.category_groups.forEach(cg => cg.categories = Mangler.find(this.details.categories, { category_group_id: cg.id }));

		// Put areas into groups
		this.details.area_groups.forEach(ag => ag.areas = Mangler.find(this.details.areas, { parent_id: ag.id }));

		this.modal.open();
	}

	save() {
		this.formatNumbers(false);
		const snapshot = Mangler.clone(this.details.task);
		this.formatNumbers(true);
		if (this.details.edit_main && snapshot.duration_type === 'min') snapshot.duration = DecimalPipe.parse(snapshot.duration) / 60;
		if (this.details.edit_lines) snapshot.lines.forEach(line => { if (line.task?.duration_type === 'min') line.task.duration = DecimalPipe.parse(line.task.duration) / 60; });
		if (Array.isArray(snapshot.subtasks)) snapshot.subtasks.forEach(sub => { if (!sub.id) sub.duration = sub.duration_type === 'min' ? DecimalPipe.parse(sub.duration) / 60 : DecimalPipe.parse(sub.duration); });
		const lockedUserIds = this.details.task.locked_users || [];
		snapshot.assigned_users = (snapshot.assigned_users || []).filter(id => !lockedUserIds.includes(id));

		this.disabled = true;
		this.api.task.save(snapshot, savedId => {
			this.disabled = false;
			if (snapshot.id === 'new') {
				this.app.notifications.showSuccess('Task created.');
			} else {
				this.app.notifications.showSuccess('Task updated.');
			}
			this.app.navigation.refreshBillableTaskCount();
			this.modal.close(savedId);
		}, error => {
			this.disabled = false;
			this.app.notifications.showDanger(error.message);
		});
	}

	deleteTask() {
		this.app.dialog.pop('Delete Task', 'Are you sure you want to delete this task?', ['Cancel', '*Delete'], btn => {
			if (btn === 1) {
				this.disabled = true;
				this.api.task.delete(this.id, () => {
					this.disabled = false;
					this.app.notifications.showSuccess('Task deleted.');
					this.app.navigation.refreshBillableTaskCount();
					this.modal.close(!this.details.task.parent_id ? 'task-deleted' : 'subtask-deleted');
				}, error => {
					this.disabled = false;
					this.app.notifications.showDanger(error.message);
				});
			}
		});
	}

	dependencyChange(data): void {
		this.details.task.dependencies = data;
	}

	assignedUsersChange(data): void {
		const lockedUserIds = this.details.task.locked_users || [];
		this.assignedUsersSelected = data;
		this.details.task.assigned_users = (data || []).filter(id => !lockedUserIds.includes(id));
	}

	markAsCompleted() {
		this.app.dialog.pop('Mark as Complete', 'Are you sure you want to mark this task as Completed?', ['Cancel', '*Submit'], btn => {
			if (btn === 1) {
				this.disabled = true;
				this.api.task.markAsCompleted(this.details.task.id, () => {
					this.disabled = false;
					this.app.notifications.showSuccess('Task Completed.');
					this.app.navigation.refreshBillableTaskCount();
					this.modal.close('completed');
				}, error => {
					this.disabled = false;
					this.app.notifications.showDanger(error.message);
				});
			}
		});
	}

	markAsActive() {
		this.app.dialog.pop('Mark as Active', 'Are you sure you want to mark this task as Active?', ['Cancel', '*Submit'], btn => {
			if (btn === 1) {
				this.disabled = true;
				this.api.task.markAsActive(this.details.task.id, () => {
					this.disabled = false;
					this.app.notifications.showSuccess('Task marked as Active.');
					this.app.navigation.refreshBillableTaskCount();
					this.modal.close('active');
				}, error => {
					this.disabled = false;
					this.app.notifications.showDanger(error.message);
				});
			}
		});
	}

	durationChanged() {
		this.details.task.duration = DecimalPipe.transform(this.details?.task.duration, 0, 4);
		this.formatNumbers();
	}

	selectTemplate(template) {
		this.modalData.task_template_id = template?.id;
		this.disabled = true;
		this.api.task.new(this.modalData, data => {
			this.disabled = false;
			this.selectTaskTemplateModal.close();
			this.processData(data);
		}, error => {
			this.disabled = false;
			this.selectTaskTemplateModal.close();
			this.app.notifications.showDanger(error.message);
		});
	}

	addSubtask(list) {
		list.push({ id: null, type: 'simple', name: '', description: '', duration: 0, duration_type: 'hour', billable: 0, dependencies: [] });
	}

	removeSubtask(list, index) {
		list.splice(index, 1);
	}

	subtaskDependencyChange(data, sub) {
		sub.dependencies = data;
	}

	subtaskDurationChanged(sub) {
		sub.duration = DecimalPipe.transform(sub.duration, 0, 4);
	}

	saveAsTemplate() {
		this.editingTemplateId = null;
		this.templateName = '';
		this.templatePhases = this.details.phases || [];
		this.templateDependencies = this.details.dependencies || [];
		// Seed the editor with the task's current subtasks (in display units), as editable new definitions
		this.templateSubtasks = (this.details.task.subtasks || []).map(sub => {
			const clone = Mangler.clone(sub);
			// Existing children hold hours; new definitions were already converted for display in processData
			if (clone.id && clone.duration_type === 'min') clone.duration *= 60;
			clone.id = null;
			clone.type = 'simple';
			return clone;
		});
		this.saveAsTaskTemplateModal.open();
	}

	editTemplate(template) {
		this.disabled = true;
		this.api.task.getTaskTemplate(template.id, data => {
			this.disabled = false;
			const tpl = data.template;
			this.editingTemplateId = template.id;
			this.templateName = tpl.template_name || '';
			this.templatePhases = data.phases || [];
			this.templateDependencies = data.dependencies || [];
			this.templateData = {
				name: tpl.name || '',
				description: tpl.description || '',
				phase_id: tpl.phase_id || null,
				duration: tpl.duration_type === 'min' ? (tpl.duration || 0) * 60 : (tpl.duration || 0),
				duration_type: tpl.duration_type || 'hour',
				billable: tpl.billable ? 1 : 0,
				dependencies: tpl.dependencies || []
			};
			this.templateSubtasks = (tpl.subtasks || []).map(sub => {
				const clone = Mangler.clone(sub);
				if (clone.duration_type === 'min') clone.duration *= 60; // stored hours -> display minutes
				clone.id = null;
				clone.type = 'simple';
				return clone;
			});
			this.saveAsTaskTemplateModal.open();
		}, error => {
			this.disabled = false;
			this.app.notifications.showDanger(error.message);
		});
	}

	saveTaskTemplate() {
		// All editor rows are display-unit definitions, so convert minutes back to hours uniformly
		const subtasks = this.templateSubtasks
			.filter(sub => (sub.name || '').trim())
			.map(sub => {
				const clone = Mangler.clone(sub);
				clone.duration = clone.duration_type === 'min' ? DecimalPipe.parse(clone.duration) / 60 : DecimalPipe.parse(clone.duration);
				return clone;
			});

		this.disabled = true;

		if (this.editingTemplateId) {
			const td = this.templateData;
			this.api.task.updateTaskTemplate({
				id: this.editingTemplateId,
				template_name: this.templateName,
				name: td.name,
				description: td.description,
				phase_id: td.phase_id,
				duration: td.duration_type === 'min' ? DecimalPipe.parse(td.duration) / 60 : DecimalPipe.parse(td.duration),
				duration_type: td.duration_type,
				billable: td.billable ? 1 : 0,
				dependencies: td.dependencies,
				subtasks
			}, () => {
				this.disabled = false;
				this.app.notifications.showSuccess('Task template updated.');
				this.saveAsTaskTemplateModal.close();
				this.api.task.listTaskTemplates(list => { this.taskTemplates = list; }, () => {});
			}, error => {
				this.disabled = false;
				this.app.notifications.showDanger(error.message);
			});
			return;
		}

		const snapshot = Mangler.clone(this.details.task);
		if (snapshot.duration_type === 'min') snapshot.duration = DecimalPipe.parse(snapshot.duration) / 60;
		snapshot.template_name = this.templateName;
		snapshot.subtasks = subtasks;

		this.api.task.createTaskTemplate(snapshot, () => {
			this.disabled = false;
			this.app.notifications.showSuccess('Task template added.');
			this.saveAsTaskTemplateModal.close();
		}, error => {
			this.disabled = false;
			this.app.notifications.showDanger(error.message);
		});
	}

	deleteTemplate(template) {
		this.app.dialog.pop('Delete Task Template', 'Are you sure you want to delete "' + template.template_name + '" task template?', ['Cancel', '*Delete'], btn => {
			if (btn === 1) {
				this.disabled = true;
				this.api.task.deleteTaskTemplate(template.id, () => {
					this.app.notifications.showSuccess('Task template deleted.');
					this.api.task.listTaskTemplates(list => {
						this.disabled = false;
						this.taskTemplates = list;
					}, () => {
						this.disabled = false;
					});
				}, error => {
					this.disabled = false;
					this.app.notifications.showDanger(error.message);
				});
			}
		});
	}

	formatNumbers(separator = true) {
		if (!this.details?.task.extra_labour) return;

		let total = 0;
		this.details.task.extra_labour.forEach(lt => {
			lt.hours = DecimalPipe.transform(lt.hours, 0, 4, separator);
			total += DecimalPipe.parse(lt.hours);
		});

		const usesMinutes = this.details.edit_main && this.details.task.duration_type === 'min';
		let durationHours = DecimalPipe.parse(this.details.task.duration);
		if (usesMinutes) durationHours /= 60;

		if (durationHours < total) {
			const newDuration = usesMinutes ? total * 60 : total;
			this.details.task.duration = DecimalPipe.transform(newDuration, 0, 4, separator);
		}
	}

}
